import { AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { DISCOUNT_CHANGES_MESSAGE, DISCOUNT_CURRENCIES, DISCOUNT_INSTRUCTION } from './constants';
import { errorMessage } from './errors';
import InstructionActions from './InstructionActions';
import {
  buildDiscountPreview, buildMarkupPreview, convertOriginalValues, exchangeMultiplier,
  localizedNumber, money, roundMoney, validatePricingSteps,
} from './pricing';
import { createStyles } from './styles';
import useQueueStatus from './useQueueStatus';

// Sekcja „Rabatowanie”: wgranie oferty, analiza, narzut/rabat i generowanie PDF.
const DiscountSection = ({ onOpenInstruction }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [resultUrl, setResultUrl] = useState('');
  const [resultName, setResultName] = useState('umowa_aluminium.pdf');
  const [analysis, setAnalysis] = useState(null);
  const [workflowMode, setWorkflowMode] = useState('discount_only');
  const [workflowStep, setWorkflowStep] = useState('discount');
  const [markupMode, setMarkupMode] = useState('markups');
  const [markups, setMarkups] = useState({});
  const [generalMarkup, setGeneralMarkup] = useState('0');
  const [markupTargetGross, setMarkupTargetGross] = useState('');
  const [discounts, setDiscounts] = useState({});
  const [mode, setMode] = useState('discounts');
  const [vat, setVat] = useState('23');
  const [generalDiscount, setGeneralDiscount] = useState('10');
  const [targetGross, setTargetGross] = useState('');
  const [changeCurrency, setChangeCurrency] = useState(false);
  const [targetCurrency, setTargetCurrency] = useState('');
  const [exchangeRate, setExchangeRate] = useState('');
  const [exchangeRateDirection, setExchangeRateDirection] = useState('source_to_target');
  const [removeOfferRecipient, setRemoveOfferRecipient] = useState(false);
  const [removeDuplicatePrices, setRemoveDuplicatePrices] = useState(false);
  const [removeTechnologist, setRemoveTechnologist] = useState(false);
  const [editNotes, setEditNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);
  const [discountInfoLanguage, setDiscountInfoLanguage] = useState('PL');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [showChangedNotice, setShowChangedNotice] = useState(false);
  const queue = useQueueStatus(busy);
  const canChangeCurrency = DISCOUNT_CURRENCIES.includes(analysis?.currency);

  // Każda zmiana parametrów unieważnia wygenerowany PDF, żeby podgląd i pobieranie
  // nigdy nie pokazywały pliku sprzed zmian.
  const discountParamsKey = JSON.stringify([
    workflowMode, markupMode, markups, generalMarkup, markupTargetGross,
    mode, discounts, generalDiscount, targetGross, vat,
    targetCurrency, exchangeRate, exchangeRateDirection,
    removeOfferRecipient, removeDuplicatePrices, removeTechnologist, editNotes, notesText,
    showDiscountInfo, discountInfoLanguage,
  ]);
  const discountParamsKeyRef = useRef(discountParamsKey);
  const resultUrlRef = useRef(resultUrl);
  discountParamsKeyRef.current = discountParamsKey;
  resultUrlRef.current = resultUrl;

  useEffect(() => {
    if (!resultUrlRef.current) return;
    setResultUrl('');
    setShowChangedNotice(false);
    setMessage({ type: 'info', text: 'Parametry zostały zmienione — wygeneruj PDF ponownie.' });
  }, [discountParamsKey]);

  useEffect(() => {
    if (!file) { setSourceUrl(''); return undefined; }
    const url = URL.createObjectURL(file);
    setSourceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => () => { if (resultUrl) URL.revokeObjectURL(resultUrl); }, [resultUrl]);

  useEffect(() => {
    if (!showChangedNotice) return undefined;
    const timer = setTimeout(() => setShowChangedNotice(false), 2500);
    return () => clearTimeout(timer);
  }, [showChangedNotice]);

  const markupPreview = useMemo(() => buildMarkupPreview({
    analysis, workflowMode, markupMode, markups, generalMarkup, markupTargetGross,
    vat, targetCurrency, exchangeRate, exchangeRateDirection,
  }), [analysis, workflowMode, markupMode, markups, generalMarkup, markupTargetGross,
    vat, targetCurrency, exchangeRate, exchangeRateDirection]);

  const preview = useMemo(() => buildDiscountPreview({
    analysis, workflowMode, markupMode, markupPreview, mode, discounts,
    generalDiscount, targetGross, vat,
  }), [analysis, workflowMode, markupMode, markupPreview, discounts,
    generalDiscount, mode, targetGross, vat]);

  const convertedOriginalValues = useMemo(() => convertOriginalValues({
    analysis, targetCurrency, exchangeRate, exchangeRateDirection,
  }), [analysis, targetCurrency, exchangeRate, exchangeRateDirection]);

  const resetModeDefaults = nextMode => {
    setMode(nextMode);
    setVat(analysis?.vat_rate ? String(analysis.vat_rate) : '23');
    setGeneralDiscount('0');
    setTargetGross('');
    if (analysis) setDiscounts(Object.fromEntries(analysis.items.map(item => [item.number, '0'])));
  };

  const resetMarkupModeDefaults = nextMode => {
    setMarkupMode(nextMode);
    setGeneralMarkup('0');
    setMarkupTargetGross('');
    if (analysis) setMarkups(Object.fromEntries(analysis.items.map(item => [item.number, '0'])));
  };

  const chooseWorkflowMode = nextMode => {
    setWorkflowMode(nextMode);
    setWorkflowStep(nextMode === 'markup_and_discount' ? 'markup' : 'discount');
    resetMarkupModeDefaults('markups');
    resetModeDefaults('discounts');
  };

  const chooseFile = event => {
    setFile(event.target.files?.[0] || null);
    setAnalysis(null); setDiscounts({}); setResultUrl(''); setMessage(null);
    setChangeCurrency(false);
    setTargetCurrency('');
    setExchangeRate('');
    setExchangeRateDirection('source_to_target');
    setWorkflowMode('discount_only');
    setWorkflowStep('discount');
    setMarkupMode('markups');
    setMarkups({});
    setGeneralMarkup('0');
    setMarkupTargetGross('');
    setShowDiscountInfo(false);
    setDiscountInfoLanguage('PL');
    // Nowy plik: czyścimy opcje czyszczenia PDF. Przy zmianie trybu rabatu/narzutu
    // zaznaczone checkboxy zostają bez zmian.
    setRemoveOfferRecipient(false);
    setRemoveDuplicatePrices(false);
    setRemoveTechnologist(false);
    setEditNotes(false);
    setNotesText('');
    resetModeDefaults('discounts');
    setVat('23');
  };

  const finish = () => {
    setFile(null);
    setAnalysis(null);
    setWorkflowMode('discount_only');
    setWorkflowStep('discount');
    setMarkupMode('markups');
    setMarkups({});
    setGeneralMarkup('0');
    setMarkupTargetGross('');
    setDiscounts({});
    setResultUrl('');
    setResultName('umowa_aluminium.pdf');
    setMessage(null);
    setShowChangedNotice(false);
    setMode('discounts');
    setVat('23');
    setGeneralDiscount('10');
    setTargetGross('');
    setChangeCurrency(false);
    setTargetCurrency('');
    setExchangeRate('');
    setExchangeRateDirection('source_to_target');
    setRemoveOfferRecipient(false);
    setRemoveDuplicatePrices(false);
    setRemoveTechnologist(false);
    setEditNotes(false);
    setNotesText('');
    setShowDiscountInfo(false);
    setDiscountInfoLanguage('PL');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const analyze = async () => {
    if (!file) return;
    setBusy(true); setMessage({ type: 'info', text: 'Analiza oczekuje w kolejce…' });
    const form = new FormData(); form.append('file', file);
    try {
      const { data } = await api.post('/aluminium-deals/analyze', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAnalysis(data);
      setEditNotes(false);
      setNotesText(data.notes?.text || '');
      setVat(String(data.vat_rate || '23'));
      setDiscounts(Object.fromEntries(data.items.map(item => [item.number, '0'])));
      setMarkups(Object.fromEntries(data.items.map(item => [item.number, '0'])));
      setMessage({ type: 'success', text: `Rozpoznano ${data.items.length} pozycji.` });
    } catch (error) { setMessage({ type: 'error', text: await errorMessage(error) }); }
    finally { setBusy(false); }
  };

  const submitProcess = async () => {
    const requestKey = discountParamsKeyRef.current;
    setBusy(true); setMessage({ type: 'info', text: 'Zlecenie oczekuje w kolejce…' });
    const form = new FormData();
    form.append('file', file); form.append('workflow_mode', workflowMode);
    form.append('mode', mode); form.append('vat_percent', vat);
    form.append('discounts', JSON.stringify(discounts));
    form.append('markups', JSON.stringify(markups));
    form.append('remove_recipient', String(removeOfferRecipient));
    form.append('remove_duplicate_prices', String(removeDuplicatePrices));
    form.append('remove_technologist', String(removeTechnologist));
    form.append('edit_notes', String(editNotes));
    if (editNotes) form.append('notes_text', notesText);
    form.append('show_discount_info', String(showDiscountInfo));
    if (showDiscountInfo) form.append('discount_info_language', discountInfoLanguage);
    if (targetCurrency) {
      form.append('target_currency', targetCurrency);
      form.append('exchange_rate', String(exchangeMultiplier(exchangeRate, exchangeRateDirection)));
    }
    if (mode === 'general_discount') form.append('general_discount', generalDiscount);
    if (mode === 'target_gross') form.append('target_gross', targetGross);
    if (workflowMode === 'markup_and_discount') {
      form.append('markup_mode', markupMode);
      if (markupMode === 'general_markup') form.append('general_markup', generalMarkup);
      if (markupMode === 'target_gross') form.append('markup_target_gross', markupTargetGross);
    }
    try {
      const response = await api.post('/aluminium-deals/process', form, {
        headers: { 'Content-Type': 'multipart/form-data' }, responseType: 'blob',
      });
      if (requestKey !== discountParamsKeyRef.current) {
        setMessage({ type: 'info', text: 'Parametry zmieniły się w trakcie przetwarzania — wygeneruj PDF ponownie.' });
        return;
      }
      const url = URL.createObjectURL(response.data);
      setResultUrl(url);
      const match = response.headers['content-disposition']?.match(/filename="?([^";]+)"?/);
      setResultName(match?.[1] || 'umowa_aluminium.pdf');
      const calculated = response.headers['x-calculated-discount'];
      const calculatedMarkup = response.headers['x-calculated-markup'];
      const operations = [
        calculatedMarkup ? `narzut ${Number(calculatedMarkup).toFixed(4)}%` : null,
        calculated ? `rabat ${Number(calculated).toFixed(4)}%` : null,
      ].filter(Boolean);
      setMessage({ type: 'success', text: operations.length ? `Gotowe. Naniesiono: ${operations.join(', ')}.` : 'Gotowe — zmieniony PDF jest dostępny poniżej.' });
      setShowChangedNotice(true);
    } catch (error) { setMessage({ type: 'error', text: await errorMessage(error) }); }
    finally { setBusy(false); }
  };

  const {
    sourcePricingGross, markupTargetNumber, markupStepInvalid, discountStepInvalid,
  } = validatePricingSteps({
    analysis, convertedOriginalValues, markupPreview, vat, changeCurrency, targetCurrency,
    exchangeRate, exchangeRateDirection, markupMode, markups, generalMarkup,
    markupTargetGross, mode, discounts, generalDiscount, targetGross,
  });

  const styles = useMemo(() => createStyles(busy), [busy]);

  const metrics = (net, vatAmount, gross, title, vatRate = vat, currency = analysis.currency) => <><h3>{title}</h3><div style={styles.metrics}>
    <div style={styles.metric}>Netto<strong style={{ display: 'block' }}>{money(net, currency)}</strong></div>
    <div style={styles.metric}>VAT {vatRate}%<strong style={{ display: 'block' }}>{money(vatAmount, currency)}</strong></div>
    <div style={styles.metric}>Brutto<strong style={{ display: 'block' }}>{money(gross, currency)}</strong></div>
  </div></>;

  const originalItemAmount = (item, type) => {
    const sourceValue = type === 'unit' ? Number(item.unit_net) : Number(item.value_net);
    const conversionRate = targetCurrency
      ? exchangeMultiplier(exchangeRate, exchangeRateDirection)
      : 0;
    if (!(conversionRate > 0)) return money(sourceValue, item.originalCurrency);
    const convertedUnit = roundMoney(Number(item.unit_net) * conversionRate);
    const convertedValue = type === 'unit'
      ? convertedUnit
      : roundMoney(Number(item.quantity) * convertedUnit);
    return <span className="aluminium-converted-original">
      <span>{money(convertedValue, targetCurrency)}</span>
      <small>({money(sourceValue, item.originalCurrency)})</small>
    </span>;
  };

  return <>
    {showChangedNotice && <div className="aluminium-changed-toast" role="status" aria-live="polite">✓ Zmieniono ceny w PDF!</div>}
    <div className="aluminium-upload-panel" style={styles.panel}>
      <div className="aluminium-module-header">
        <div>
          <h3 className="aluminium-automation-title">Rabatowanie oferty</h3>
        </div>
        <InstructionActions instruction={DISCOUNT_INSTRUCTION} onPreview={onOpenInstruction} />
      </div>
      <aside className="aluminium-changes-notice" aria-labelledby="discount-changes-title">
        <AlertTriangle aria-hidden="true" />
        <div>
          <strong id="discount-changes-title">Zmiany</strong>
          <time dateTime={DISCOUNT_CHANGES_MESSAGE.dateTime}>{DISCOUNT_CHANGES_MESSAGE.dateLabel}</time>
          <span>Dodano:</span>
          <ul>
            {DISCOUNT_CHANGES_MESSAGE.items.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </aside>
      <div style={styles.row}>
      <label className={`aluminium-file-button ${busy ? 'disabled' : ''}`} aria-disabled={busy}>
        {/* <span aria-hidden="true">☁</span> */}
        Wybierz ofertę
        <input ref={fileInputRef} className="aluminium-visually-hidden-input" type="file" accept="application/pdf,.pdf" onChange={chooseFile} disabled={busy} />
      </label>
      {file && <span className="aluminium-selected-file">{file.name}</span>}
      <button style={styles.button} onClick={analyze} disabled={!file || busy}>{busy ? 'Przetwarzanie…' : 'Analizuj'}</button>
      {busy && <small>{queue.active ? `Kolejka pracuje (${queue.waiting} oczekuje)` : 'Oczekiwanie na kolejkę…'}</small>}
    </div>{message && <p style={{ color: message.type === 'error' ? '#b91c1c' : message.type === 'success' ? '#15803d' : '#1d4ed8' }}>{message.text}</p>}</div>

    {!file && !analysis && !busy && <section className="aluminium-faq" style={styles.panel} aria-labelledby="discount-faq-title">
      <h3 id="discount-faq-title">FAQ</h3>
      <details>
        <summary>OSTATNIE AKTUALIZACJE</summary>
        <div className="aluminium-updates">
          <p>
            <time dateTime="2026-08-15T13:20:51">15.08.2026 13:20:51</time>
            <span>Poprawiono intefejs użytkownika po ostatnich zmianach.</span>
          </p>
          <p>
            <time dateTime="2026-08-12T13:20:51">15.08.2026 13:20:51</time>
            <span>Wdrożono możliwosc przewalutowania (PLN/CZK/EURO).</span>
          </p>
          <p>
            <time dateTime="2026-08-12T16:41:23">12.08.2026 16:41:23</time>
            <span>Wdrożono możliwość przewalutowania ofert (PLN &rarr; EUR/CZK).</span>
          </p>
          <p>
            <time dateTime="2026-08-12T11:48:56">12.08.2026 11:48:56</time>
            <span>Wdrożono obsługę oferty w języku czeskim.</span>
          </p>
          <p>
            <time dateTime="2026-08-11T18:28:09">11.08.2026 20:44:11</time>
            <span>Wdrożono możliwość usunięcia zduplikowanych cen w każdej z pozycji.</span>
          </p>
          <p>
            <time dateTime="2026-08-11T18:28:09">11.08.2026 20:11:02</time>
            <span>Wdrożono możliwość usunięcia pustych danych adresata.</span>
          </p>
        </div>
      </details>
      <details>
        <summary>Jaki plik tutaj załączamy?</summary>
        <p>Należy załączyć plik ofertowy z cenami konstrukcji.</p>
      </details>
      <details>
        <summary>Gdzie złosić błąd lub propozycję wdrożenia nowej funkcjonalności?</summary>
        <p>aberdowski@matplast.pl</p>
      </details>
    </section>}

    {analysis && <div className="aluminium-config-panel" style={styles.panel}>
      <fieldset className="aluminium-currency-decision">
        <legend>
          Oferta jest sporządzona w rozliczeniu {analysis.currency}. Czy chcesz zmienić
          docelową walutę (np. na {DISCOUNT_CURRENCIES.filter(currency => currency !== analysis.currency).join('/')})?
        </legend>
        <div className="aluminium-currency-options">
          <label>
            <input
              type="radio"
              name="change-currency"
              checked={!changeCurrency}
              onChange={() => {
                setChangeCurrency(false);
                setTargetCurrency('');
                setExchangeRate('');
                setExchangeRateDirection('source_to_target');
              }}
            />
            Nie
          </label>
          <label>
            <input
              type="radio"
              name="change-currency"
              checked={changeCurrency}
              disabled={!canChangeCurrency}
              onChange={() => setChangeCurrency(true)}
            />
            Tak
          </label>
        </div>
        {changeCurrency && <div className="aluminium-currency-conversion">
          <label>Waluta docelowa <select style={styles.input} value={targetCurrency} onChange={e => { setTargetCurrency(e.target.value); setExchangeRate(''); setExchangeRateDirection('source_to_target'); }}>
            <option value="">Wybierz walutę</option>
            {DISCOUNT_CURRENCIES.filter(currency => currency !== analysis.currency).map(currency => (
              <option key={currency} value={currency}>{currency}</option>
            ))}
          </select></label>
          {targetCurrency && <label className="aluminium-exchange-rate"><select aria-label="Sposób podania kursu" value={exchangeRateDirection} onChange={e => { setExchangeRateDirection(e.target.value); setExchangeRate(''); }}>
            <option value="source_to_target">1 {analysis.currency} =</option>
            <option value="target_to_source">1 {targetCurrency} =</option>
          </select><input style={styles.input} aria-label="Kurs waluty" type="number" min="0.000001" step="0.000001" value={exchangeRate} onChange={e => setExchangeRate(e.target.value)} placeholder={exchangeRateDirection === 'target_to_source' ? analysis.currency : targetCurrency} /> {exchangeRateDirection === 'target_to_source' ? analysis.currency : targetCurrency}</label>}
        </div>}
        {!canChangeCurrency && <p className="aluminium-message error">Nie rozpoznano obsługiwanej waluty oferty — zmiana waluty jest niedostępna.</p>}
      </fieldset>
      <div className="aluminium-original-values aluminium-original-values-summary">
        {metrics(analysis.total_net, analysis.vat, analysis.total_gross, 'Wartości pierwotne', analysis.vat_rate, analysis.currency)}
        {convertedOriginalValues && metrics(
          convertedOriginalValues.net,
          convertedOriginalValues.vat,
          convertedOriginalValues.gross,
          'Wartości pierwotne po przewalutowaniu',
          analysis.vat_rate,
          targetCurrency,
        )}
      </div>
      <div className="aluminium-workflow-content">
      <h3>Tryb pracy</h3>
      <div className="aluminium-mode-switcher aluminium-workflow-switcher" role="group" aria-label="Tryb zmiany cen">
        <button type="button" className={`aluminium-mode-button ${workflowMode === 'discount_only' ? 'active' : ''}`} aria-pressed={workflowMode === 'discount_only'} onClick={() => chooseWorkflowMode('discount_only')}><span>🏷️</span> Tylko rabat</button>
        <button type="button" className={`aluminium-mode-button ${workflowMode === 'markup_and_discount' ? 'active' : ''}`} aria-pressed={workflowMode === 'markup_and_discount'} onClick={() => chooseWorkflowMode('markup_and_discount')}><span>📈</span> Narzut + rabat</button>
      </div>
      </div>
      <div className="aluminium-workflow-details">
      <div className="aluminium-value-controls aluminium-discount-controls">
        <label>VAT (%) <input style={styles.input} type="number" min="0" max="23" step="0.01" value={vat} onChange={e => setVat(e.target.value)} /></label>
        {!analysis.vat_rate_detected && <p className="aluminium-form-error">Nie udało się odczytać stawki VAT z oferty — sprawdź ją ręcznie (ustawiono 23%).</p>}
        {analysis.vat_rate_detected && String(analysis.vat_rate) !== '23' && <p className="aluminium-form-error">Uwaga: oferta ma stawkę VAT {analysis.vat_rate}% (inną niż standardowe 23%).</p>}
        {analysis.vat_rate_detected && String(analysis.vat_rate) === '23' && localizedNumber(vat) !== 23 && <p className="aluminium-form-error">Uwaga: zmieniono VAT względem oferty (w ofercie: 23%).</p>}
      </div>

      {workflowMode === 'markup_and_discount' && workflowStep === 'markup' && <section className="aluminium-pricing-step">
        <div className="aluminium-step-heading"><span>Krok 1 z 2</span><h3>Ustal narzut</h3></div>
        <div className="aluminium-mode-switcher" role="group" aria-label="Sposób naliczania narzutu">
          <button type="button" className={`aluminium-mode-button ${markupMode === 'markups' ? 'active' : ''}`} aria-pressed={markupMode === 'markups'} onClick={() => resetMarkupModeDefaults('markups')}><span>📋</span> Narzut dla pozycji</button>
          <button type="button" className={`aluminium-mode-button ${markupMode === 'general_markup' ? 'active' : ''}`} aria-pressed={markupMode === 'general_markup'} onClick={() => resetMarkupModeDefaults('general_markup')}><span>📈</span> Narzut ogólny</button>
          <button type="button" className={`aluminium-mode-button ${markupMode === 'target_gross' ? 'active' : ''}`} aria-pressed={markupMode === 'target_gross'} onClick={() => resetMarkupModeDefaults('target_gross')}><span>🎯</span> Docelowa kwota brutto</button>
        </div>
        <div className="aluminium-value-controls aluminium-discount-controls">
          {markupMode === 'general_markup' && <label>Narzut ogólny (%) <input style={styles.input} type="number" min="0" step="0.01" value={generalMarkup} onChange={e => setGeneralMarkup(e.target.value)} /></label>}
          {markupMode === 'target_gross' && <label>Kwota brutto po narzucie <input style={styles.input} type="number" min="0.01" step="0.01" value={markupTargetGross} onChange={e => setMarkupTargetGross(e.target.value)} /></label>}
        </div>
        {markupMode === 'target_gross' && markupTargetNumber > 0 && markupTargetNumber < sourcePricingGross && <p className="aluminium-form-error">Docelowa kwota po narzucie nie może być niższa od wartości przed narzutem ({money(sourcePricingGross, targetCurrency || analysis.currency)}).</p>}
        {markupMode === 'target_gross' && markupPreview.automaticPercentage !== null && markupTargetNumber >= sourcePricingGross && <div className="aluminium-auto-discount aluminium-auto-markup"><span>Automatyczny narzut bazowy</span><strong>{markupPreview.automaticPercentage.toFixed(4)}%</strong></div>}
        <div style={{ overflowX: 'auto' }}><table style={styles.table}><thead><tr><th style={styles.cell}>Pozycja</th><th style={styles.cell}>Ilość</th><th style={styles.cell}>Cena pierwotna netto</th><th style={styles.cell}>Wartość pierwotna netto</th><th style={styles.cell}>Narzut</th><th style={styles.cell}>Cena po narzucie</th><th style={styles.cell}>Wartość po narzucie</th></tr></thead><tbody>
          {markupPreview.rows.map(item => <tr key={item.number}><td style={styles.cell}>{item.number}</td><td style={styles.cell}>{item.quantity}</td><td style={styles.cell}>{originalItemAmount(item, 'unit')}</td><td style={styles.cell}>{originalItemAmount(item, 'value')}</td><td style={styles.cell}>{markupMode === 'markups' ? <input style={styles.input} type="number" min="0" step="0.01" value={markups[item.number]} onChange={e => setMarkups({ ...markups, [item.number]: e.target.value })} /> : `${Number(item.adjustment || 0).toFixed(4)}%`}</td><td style={styles.cell}>{money(item.priceUnit, item.currency)}</td><td style={styles.cell}>{money(item.priceValue, item.currency)}</td></tr>)}
        </tbody></table></div>
        {metrics(markupPreview.net, markupPreview.vat, markupPreview.gross, 'Wartości po narzucie', vat, targetCurrency || analysis.currency)}
        <button style={styles.button} type="button" disabled={busy || markupStepInvalid} onClick={() => setWorkflowStep('discount')}>Dalej: ustaw rabat</button>
      </section>}

      {(workflowMode === 'discount_only' || workflowStep === 'discount') && <section className="aluminium-pricing-step">
        <div className="aluminium-step-heading"><span>{workflowMode === 'markup_and_discount' ? 'Krok 2 z 2' : 'Rabat'}</span><h3>Ustal rabat</h3></div>
        {workflowMode === 'markup_and_discount' && metrics(markupPreview.net, markupPreview.vat, markupPreview.gross, 'Wartości bazowe po narzucie', vat, targetCurrency || analysis.currency)}
        <div className="aluminium-mode-switcher" role="group" aria-label="Sposób rabatowania">
          <button type="button" className={`aluminium-mode-button ${mode === 'discounts' ? 'active' : ''}`} aria-pressed={mode === 'discounts'} onClick={() => resetModeDefaults('discounts')}><span>🏷️</span> Rabat dla pozycji</button>
          <button type="button" className={`aluminium-mode-button ${mode === 'general_discount' ? 'active' : ''}`} aria-pressed={mode === 'general_discount'} onClick={() => resetModeDefaults('general_discount')}><span>📉</span> Rabat ogólny</button>
          <button type="button" className={`aluminium-mode-button ${mode === 'target_gross' ? 'active' : ''}`} aria-pressed={mode === 'target_gross'} onClick={() => resetModeDefaults('target_gross')}><span>🎯</span> Docelowa kwota brutto</button>
        </div>
        <div className="aluminium-value-controls aluminium-discount-controls">
          {mode === 'general_discount' && <label>Rabat ogólny (%) <input style={styles.input} type="number" min="0" max="100" step="0.01" value={generalDiscount} onChange={e => setGeneralDiscount(e.target.value)} /></label>}
          {mode === 'target_gross' && <label>Kwota brutto po rabacie <input style={styles.input} type="number" min="0.01" step="0.01" value={targetGross} onChange={e => setTargetGross(e.target.value)} /></label>}
        </div>
        {mode === 'target_gross' && localizedNumber(targetGross) > markupPreview.gross && <p className="aluminium-form-error">Docelowa kwota po rabacie nie może być wyższa od wartości bazowej ({money(markupPreview.gross, targetCurrency || analysis.currency)}).</p>}
        {mode === 'target_gross' && preview.automaticDiscount !== null && localizedNumber(targetGross) <= markupPreview.gross && <div className="aluminium-auto-discount"><span>Automatyczny rabat bazowy</span><strong>{preview.automaticDiscount.toFixed(4)}%</strong></div>}
        <div style={{ overflowX: 'auto' }}><table style={styles.table}><thead><tr><th style={styles.cell}>Pozycja</th><th style={styles.cell}>Ilość</th><th style={styles.cell}>{workflowMode === 'markup_and_discount' ? 'Cena po narzucie' : 'Cena pierwotna netto'}</th><th style={styles.cell}>{workflowMode === 'markup_and_discount' ? 'Wartość po narzucie' : 'Wartość pierwotna netto'}</th><th style={styles.cell}>Rabat</th><th style={styles.cell}>Nowa cena netto</th><th style={styles.cell}>Nowa wartość netto</th></tr></thead><tbody>
          {preview.rows.map(item => <tr key={item.number}><td style={styles.cell}>{item.number}</td><td style={styles.cell}>{item.quantity}</td><td style={styles.cell}>{workflowMode === 'markup_and_discount' ? money(item.stageBaseUnit, item.currency) : originalItemAmount(item, 'unit')}</td><td style={styles.cell}>{workflowMode === 'markup_and_discount' ? money(item.stageBaseValue, item.currency) : originalItemAmount(item, 'value')}</td><td style={styles.cell}>{mode === 'discounts' ? <input style={styles.input} type="number" min="0" max="100" step="0.01" value={discounts[item.number]} onChange={e => setDiscounts({ ...discounts, [item.number]: e.target.value })} /> : `${Number(item.discount || 0).toFixed(4)}%`}</td><td style={styles.cell}>{money(item.newUnit, item.currency)}</td><td style={styles.cell}>{money(item.newValue, item.currency)}</td></tr>)}
        </tbody></table></div>
        <div className="aluminium-discount-summary">
          {metrics(preview.net, preview.vat, preview.gross, 'Wartości po rabacie', vat, targetCurrency || analysis.currency)}
        </div>
        <label className="aluminium-recipient-option">
          <input type="checkbox" checked={removeOfferRecipient} onChange={event => setRemoveOfferRecipient(event.target.checked)} disabled={busy} />
          Usuń puste dane adresata z oferty
        </label>
        <label className="aluminium-recipient-option">
          <input type="checkbox" checked={removeDuplicatePrices} onChange={event => setRemoveDuplicatePrices(event.target.checked)} disabled={busy} />
          Usuń zduplikowane ceny z oferty
        </label>
        <label className="aluminium-recipient-option">
          <input type="checkbox" checked={removeTechnologist} onChange={event => setRemoveTechnologist(event.target.checked)} disabled={busy} />
          Usuń informacje o technologu
        </label>
        <label className="aluminium-recipient-option">
          <input type="checkbox" checked={showDiscountInfo} onChange={event => setShowDiscountInfo(event.target.checked)} disabled={busy} />
          Dodaj informacje o rabacie
        </label>
        {showDiscountInfo && <div className="aluminium-discount-language">
          <label>Język informacji o rabacie <select style={styles.input} value={discountInfoLanguage} onChange={event => setDiscountInfoLanguage(event.target.value)} disabled={busy}>
            <option value="PL">PL - Oferta z rabatem</option>
            <option value="ENG">ENG - Discounted offer</option>
            <option value="FR">FR - L'offre avec des réductions</option>
            <option value="DE">DE - Das Angebot mit Rabatten</option>
            <option value="CZ">CZ - Nabídka se slevami</option>
          </select></label>
        </div>}
        <label className="aluminium-recipient-option">
          <input type="checkbox" checked={editNotes} onChange={event => setEditNotes(event.target.checked)} disabled={busy || !analysis.notes} />
          Dodaj informację do uwag
        </label>
        {!analysis.notes && <p className="aluminium-notes-hint">Nie znaleziono sekcji Uwagi:, Notes: ani ACHTUNG: w tej ofercie.</p>}
        {editNotes && <div className="aluminium-notes-editor">
          <label htmlFor="aluminium-notes-text">Treść uwag ({analysis.notes.label})</label>
          <textarea id="aluminium-notes-text" value={notesText} onChange={event => setNotesText(event.target.value)} disabled={busy} rows={5} maxLength={3000} />
          <small>Możesz dopisać, zmienić lub usunąć tekst. Puste pole usunie obecną treść uwag z PDF.</small>
        </div>}
        <div className="aluminium-pricing-actions">
          {workflowMode === 'markup_and_discount' && <button type="button" className="aluminium-finish-button" onClick={() => setWorkflowStep('markup')}>← Wróć do narzutu</button>}
          <button style={styles.button} onClick={submitProcess} disabled={busy || discountStepInvalid}>Zmień ceny w PDF</button>
        </div>
      </section>}
      </div>
      {sourceUrl && <div className="aluminium-inline-preview"><h3>{resultUrl ? 'Podgląd zmienionej oferty' : 'Podgląd załączonej oferty'}</h3>
        <iframe className="aluminium-pdf-frame" title="Podgląd oferty PDF" src={resultUrl || sourceUrl} />
        {resultUrl && <div className="aluminium-result-actions">
          <a href={resultUrl} download={resultName} style={{ ...styles.button, display: 'inline-block', textDecoration: 'none' }}>Pobierz zmieniony PDF</a>
          <button type="button" className="aluminium-finish-button" onClick={finish}>Zakończ</button>
        </div>}
      </div>}
    </div>}

    {!analysis && sourceUrl && <div className="aluminium-preview-panel" style={styles.panel}><h3>Podgląd załączonej oferty</h3>
      <iframe className="aluminium-pdf-frame" title="Podgląd oferty PDF" src={sourceUrl} />
    </div>}
  </>;
};

export default DiscountSection;
